//* 부고전송페이지
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  bugoAttributionForCreate,
  bugoAttributionForUpdate,
} from '@service/bugo/utils/bugoAttribution.util';
import { FeventFormUseMode } from '@service/bugo/utils/feventAddEdit.util';
import { createBugo, sendBugo, updateBugo } from '@shared/api/bugo/bugo.controller';
import { feventHookUrl } from '@shared/api/fevent/fevent.hook';
import { FeventOne } from '@shared/api/fevent/fevent.interface';
import { updateMember } from '@shared/api/member/member.controller';
import { BottomPopup } from '@shared/components/BottomPopup';
import { Button } from '@shared/components/Button';
import { LoadingModal } from '@shared/components/LoadingModal';
import Modal from '@shared/components/Modal';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { defaultApiMutationOptions } from '@shared/plugIn/apiMutation';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Gender } from '@shared/types';
import { formatFeventDateString } from '@shared/utils/formatDate';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  MemberBugoIFormInput,
  MemberBugoIFormInputs,
  MemberBugoSendFormInputsEnum,
} from '../../interfaces/memberBugoSend.interface';
import { MemberBugoSender } from '../MemberBugoSend/MemberBugoSender';

interface Props {
  fevent: FeventOne;
  useMode?: FeventFormUseMode;
}

const FeventSendBugoForm: FC<Props> = function FeventSendBugoForm({
  fevent,
  useMode,
}: Props) {
  const { bugoRole } = useAuth();
  //*useForm 선언
  const { register, handleSubmit, setValue, control } = useForm<MemberBugoIFormInputs>();
  const [, setSearchParams] = useSearchParams();

  const { userProfile } = useAuth();
  const memberOrderList = fevent.memberOrderList;
  const navigate = useNavigate();
  const memberHasPhoneNumberList = memberOrderList?.filter(
    (member) => member?.phoneNumber,
  );

  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const queryClient = useQueryClient();
  const { isLoading: sendingBugo, mutateAsync: mutateSendAsync } = useMutation(
    defaultApiMutationOptions,
    {
      onSuccess: () => {
        if (fevent) {
          notificateSuccess(notifiacationInstance, '부고안내 전송 완료');
          queryClient.invalidateQueries(feventHookUrl(fevent._id, bugoRole()));
        }
      },
    },
  );

  // _id 세팅
  useEffect(() => {
    memberHasPhoneNumberList.map((member, index) => {
      setValue(`memberBugo.${index}._id`, member._id);
      setValue(`memberBugo.${index}.bugo`, member.bugo);
      setValue(`memberBugo.${index}.bugoId`, member.bugo?._id);
      setValue(`memberBugo.${index}.mournerText`, member.bugo?.mournerText);
      setValue(`memberBugo.${index}.memberId`, member._id);
      setValue(`memberBugo.${index}.phoneNumber`, member.phoneNumber);
    });
  }, [memberHasPhoneNumberList, setValue]);

  const getBugoIdListAfterUpdateBugo = async (
    memberBugoIFormInputs: MemberBugoIFormInput[],
    sendMode?: boolean,
  ) => {
    /*
    * member 별로
    0. bugo가 있는지 확인
    1. bugo가 없다면 bugo 생성
    2. bugo가 있다면 update 여부 확인 후 bugo 업데이트
     */
    if (userProfile) {
      const bugoIdList = await Promise.all(
        memberBugoIFormInputs.map(async (memberBugoInput) => {
          if (memberBugoInput.memberId) {
            const memberUpdate = {
              _id: memberBugoInput.memberId,
              phoneNumber: memberBugoInput.phoneNumber,
            };
            await updateMember(memberUpdate, bugoRole());
          }

          let bugoId = memberBugoInput.bugoId;
          if (memberBugoInput.bugoId) {
            const bugoUpdate = {
              _id: memberBugoInput.bugoId,
              mournerText: memberBugoInput[MemberBugoSendFormInputsEnum.MournerText],
              //* 관리자(Admin) 대리 작업일 때만 상가(fevent) 귀속으로 정렬 (오귀속 교정)
              ...bugoAttributionForUpdate(fevent, userProfile),
            };
            const bugo = await updateBugo(bugoUpdate, bugoRole());

            if (sendMode && bugo.lastSentPhoneNumber === memberBugoInput.phoneNumber) {
              return null;
            }
          } else {
            const bugoCreate = {
              //* 관리자(Admin)가 대신 전송할 때만 상가의 담당 지도사·브랜드에 귀속
              //* (일반 role은 기존 그대로 로그인 계정 귀속)
              ...bugoAttributionForCreate(fevent, userProfile),
              fevent: fevent._id,
              member: memberBugoInput._id,
              mournerText: memberBugoInput[MemberBugoSendFormInputsEnum.MournerText],
              schedule: {
                courtesySend: false,
              },
              config: {
                isEnable: true,
              },
            };
            const bugo = await createBugo(bugoCreate, bugoRole());
            bugoId = bugo._id;
            // 마지막에 안보낸것만 보내도록
            if (sendMode && bugo.lastSentPhoneNumber === memberBugoInput.phoneNumber) {
              return null;
            }
          }
          return bugoId;
        }),
      );
      return bugoIdList;
    }
  };

  //* 전체 부고 발송 함수
  const sendBugoFunc = async (memberBugoIFormInputs: MemberBugoIFormInput[]) => {
    const bugoIdList = await getBugoIdListAfterUpdateBugo(memberBugoIFormInputs, true);
    if (bugoIdList) {
      const bugoIds = bugoIdList.filter((item) => {
        if (item) {
          return true;
        } else return false;
      }) as string[];
      const result = await sendBugo(bugoIds, bugoRole());
      console.log('message', result);
    }
  };

  const onSubmit: SubmitHandler<MemberBugoIFormInputs> = async (data) => {
    await mutateSendAsync(sendBugoFunc(data.memberBugo));
  };

  //* 부고 전송 전 고인정보 확인 모달
  const confirmOpenId = `sendBugo-confirm-${fevent._id}`;
  const setConfirmModalOpen = useSetRecoilState(simpleOpenAtomFamily(confirmOpenId));
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({
    name: false,
    sex: false,
    deathDate: false,
  });

  const allChecked = checkedItems.name && checkedItems.sex && checkedItems.deathDate;

  const handleCheckChange = useCallback((key: string) => {
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const resetChecks = useCallback(() => {
    setCheckedItems({ name: false, sex: false, deathDate: false });
  }, []);

  const deathDateDisplay = useMemo(() => {
    const death = fevent.deceasedInfo?.death;
    if (death?.date) {
      return formatFeventDateString(death, false, true);
    }
    return '미정';
  }, [fevent.deceasedInfo?.death]);

  const previewOpenId = `sendBugo-preview-${fevent._id}`;
  const setPreviewModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(`sendBugo-preview-${fevent._id}`),
  );

  //* 미리보기 클릭 -> 기존에 저장해둔 설정 다 저장(onSubmit) 후 해당 bugoId로 navigate
  const bugoPreviewMemberClick = async (
    index: number,
    memberBugoIFormInputs: MemberBugoIFormInput[],
  ) => {
    const bugoIdList = await getBugoIdListAfterUpdateBugo(memberBugoIFormInputs);
    const bugoId = bugoIdList && bugoIdList[index];
    if (bugoId) {
      navigate(`/bugo/${bugoIdList[index]}/preview`);
    } else {
      // console.log('error bugo preview');
    }
  };

  const headerText = useMemo(() => {
    if (memberHasPhoneNumberList.length > 0) {
      return (
        <div className="ml-2 mb-3 text-sm font-bold">
          <p>상주님께 부고를 전송합니다</p>
          <p className="text-12 font-medium">
            휴대번호가 작성된 상주님께 카톡, 문자 둘다 전송됩니다
          </p>
        </div>
      );
    } else {
      return (
        <div className="break-keep text-center text-sm font-medium">
          <p>휴대번호가 존재하는 상주님이 존재하지 않습니다</p>
          <p>
            아래 <span className="font-bold">상주정보 수정 버튼</span>을 클릭하여 주세요
          </p>
        </div>
      );
    }
  }, [memberHasPhoneNumberList.length]);

  return (
    <div>
      {headerText}
      <form>
        <MemberBugoSender
          fevent={fevent}
          members={memberHasPhoneNumberList}
          register={register}
          control={control}
        />
        <div className="mt-8 space-y-4">
          <Button
            type="button"
            className="button-rectangle text-white theme-bg-13"
            onClick={() => {
              //Add mode 일때
              if (useMode === FeventFormUseMode.Add) {
                setSearchParams({ feventId: fevent._id, step: '0' });
              }
              // useMode Edit 또는 없을경우
              else {
                navigate(`/fevent/${fevent._id}/edit/?step=0`);
              }
            }}
          >
            부고장 수정
          </Button>

          <Button
            type="button"
            onClick={() => {
              resetChecks();
              setConfirmModalOpen(true);
            }}
            className="button-rectangle text-white theme-bg-main disabled:theme-text-3 disabled:theme-bg-10"
            disabled={memberHasPhoneNumberList?.length === 0 || sendingBugo}
          >
            {useMode === FeventFormUseMode.Add
              ? '상주님께 부고전송'
              : '수정·추가된 부고전송'}
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              className="button-rectangle text-white theme-bg-12"
              onClick={() => {
                setPreviewModalOpen(true);
              }}
            >
              부고장 미리보기
            </Button>
            <div>
              <Link to="/">
                <Button className="button-rectangle text-white theme-bg-12">
                  완료(홈으로)
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </form>

      <BottomPopup openId={previewOpenId}>
        <div className="w-screen rounded-t-2xl p-6 theme-text-9 theme-bg-1">
          {/* Title */}
          <h4 className="text-center text-sm font-bold">상주님 선택</h4>
          {/* Item List */}
          <ul className="max-h-64 overflow-y-auto pt-3 pb-4">
            {memberHasPhoneNumberList?.map((member, index) => (
              <li
                key={`select-${member?._id}`}
                onClick={(e) => {
                  handleSubmit((data) => {
                    const inputs = data.memberBugo;
                    bugoPreviewMemberClick(index, inputs);
                  })(e);
                }}
                className={`cursor-pointer border-b py-3 text-center leading-5 theme-border-1`}
              >
                {member?.fullName}
              </li>
            ))}
          </ul>
        </div>
      </BottomPopup>
      {/* 고인정보 확인 모달 */}
      <Modal openId={confirmOpenId} position="center">
        <div className="text-center">
          <h3 className="text-base font-bold">고인정보 확인</h3>
          <p className="mt-1 text-xs theme-text-9">
            전송 전 아래 정보가 정확한지 확인해주세요
          </p>

          <ul className="mt-5 space-y-3 text-left">
            <li
              className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3 transition-colors ${
                checkedItems.name
                  ? 'border-green-500 bg-green-50'
                  : 'theme-bg-1 theme-border-1'
              }`}
              onClick={() => handleCheckChange('name')}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
                  checkedItems.name
                    ? 'border-green-500 bg-green-500 text-white'
                    : 'border-gray-300 text-gray-300'
                }`}
              >
                ✓
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold theme-text-9">고인 성함</p>
                <p className="mt-1 text-xl font-bold">
                  {fevent.deceasedInfo?.name || '-'}
                </p>
              </div>
            </li>

            <li
              className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3 transition-colors ${
                checkedItems.sex
                  ? 'border-green-500 bg-green-50'
                  : 'theme-bg-1 theme-border-1'
              }`}
              onClick={() => handleCheckChange('sex')}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
                  checkedItems.sex
                    ? 'border-green-500 bg-green-500 text-white'
                    : 'border-gray-300 text-gray-300'
                }`}
              >
                ✓
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold theme-text-9">성별</p>
                <p className="mt-1 text-xl font-bold">
                  {fevent.deceasedInfo?.sex === Gender.Male
                    ? '남성'
                    : fevent.deceasedInfo?.sex === Gender.Femail
                    ? '여성'
                    : '-'}
                </p>
              </div>
            </li>

            <li
              className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3 transition-colors ${
                checkedItems.deathDate
                  ? 'border-green-500 bg-green-50'
                  : 'theme-bg-1 theme-border-1'
              }`}
              onClick={() => handleCheckChange('deathDate')}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
                  checkedItems.deathDate
                    ? 'border-green-500 bg-green-500 text-white'
                    : 'border-gray-300 text-gray-300'
                }`}
              >
                ✓
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold theme-text-9">별세일</p>
                <p className="mt-1 text-xl font-bold">{deathDateDisplay}</p>
              </div>
            </li>
          </ul>

          {!allChecked && (
            <button
              type="button"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-green-500 px-4 py-1.5 text-sm font-bold text-green-600 transition-colors active:bg-green-50"
              onClick={() => setCheckedItems({ name: true, sex: true, deathDate: true })}
            >
              <span>✓</span>
              모두 확인
            </button>
          )}

          <div className="mt-5 grid grid-cols-2 gap-2">
            <Button
              type="button"
              className="button-rectangle text-white theme-bg-12"
              onClick={() => {
                setConfirmModalOpen(false);
                if (useMode === FeventFormUseMode.Add) {
                  setSearchParams({ feventId: fevent._id, step: '0' });
                } else {
                  navigate(`/fevent/${fevent._id}/edit/?step=0`);
                }
              }}
            >
              수정하기
            </Button>
            <Button
              type="button"
              className="button-rectangle text-white theme-bg-main disabled:opacity-40"
              disabled={!allChecked}
              onClick={(e) => {
                setConfirmModalOpen(false);
                handleSubmit(onSubmit)(e);
              }}
            >
              확인 후 전송
            </Button>
          </div>
        </div>
      </Modal>

      <LoadingModal open={sendingBugo} title="부고 전송중"></LoadingModal>
    </div>
  );
};

export default FeventSendBugoForm;
